import { useState, useEffect, useMemo } from "react";
import { useFormContext, useForm, Controller } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useGetAllWorkoutQuery } from "@/services/workoutApi";
import { useGetCoachByIdQuery } from "@/services/coachApi";
import { useAssignWorkoutPlanMutation, useGetConsultationWorkoutPlanQuery, useGetGoalQuery } from "@/services/consultationApi";
import { Consultation, HistoryConsultation } from "@/types/consultation";
import { WorkoutPlanView } from "@/types/workout";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { HistoryFilter } from "../history-filter";
import { workoutGoals } from "@/lib/constants/workout";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import useTableData from "@/components/ui/table/data-table-logic";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { PlusIcon, CalendarIcon, Dumbbell, Target } from "lucide-react";
import EmbeddedWorkoutWizard from "@/components/admin/workoutplan/modal/embedded-workout-wizard";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { toast } from "@/components/ui/use-toast";

interface FormStepProps {
  action: "add" | "edit";
  open: boolean;
}

interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_key: string;
  sort_order: string;
  search_key?: string;
  [key: string]: string | number | (string | number)[] | null | undefined;
}

const initialValue: SearchCriteriaType = {
  limit: 10,
  offset: 0,
  sort_order: "asc",
  sort_key: "id",
};

// Days of the week — value matches the API's 0-based Monday index
const DAYS_OF_WEEK = [
  { label: "Mon", value: 0 },
  { label: "Tue", value: 1 },
  { label: "Wed", value: 2 },
  { label: "Thu", value: 3 },
  { label: "Fri", value: 4 },
  { label: "Sat", value: 5 },
  { label: "Sun", value: 6 },
];

interface PlanWorkoutForm {
  start_date: string;
  training_days: number[];
  weeks: number;
}

// ─── Plan Workout Sheet ────────────────────────────────────────────────────────

interface PlanWorkoutSheetProps {
  open: boolean;
  onClose: (workoutId?: number) => void;
  workout: WorkoutPlanView | null;
  orgId: number;
  coachId: number | undefined;
  memberId: number | undefined;
  consultationId: number | undefined;
  existingConsultationPlans?: Array<{
    id: number;
    workout_id: number;
    start_date: string;
    training_days: number[];
    weeks: number;
  }>;
}

const PlanWorkoutSheet = ({
  open,
  onClose,
  workout,
  orgId,
  coachId,
  memberId,
  consultationId,
  existingConsultationPlans,
}: PlanWorkoutSheetProps) => {
  const [assignWorkoutPlan, { isLoading }] = useAssignWorkoutPlanMutation();

  // Find the saved plan for this specific workout from the consultation's plans
  const existingPlan = existingConsultationPlans?.find(
    (p) => p.workout_id === Number(workout?.id)
  ) ?? null;

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<PlanWorkoutForm>({
    defaultValues: {
      start_date: "",
      training_days: [],
      weeks: 1,
    },
  });

  // Pre-fill form from the consultation's saved plan for this workout
  useEffect(() => {
    if (open && existingPlan) {
      reset({
        start_date: existingPlan.start_date || "",
        training_days: existingPlan.training_days || [],
        weeks: existingPlan.weeks || 1,
      });
    } else if (open) {
      reset({ start_date: "", training_days: [], weeks: 1 });
    }
  }, [open, existingPlan, reset]);

  // Reset form when sheet closes
  useEffect(() => {
    if (!open) reset({ start_date: "", training_days: [], weeks: 1 });
  }, [open, reset]);

  const trainingDays = watch("training_days");
  const weeks = watch("weeks");

  const toggleDay = (day: number) => {
    const current = trainingDays ?? [];
    if (current.includes(day)) {
      setValue("training_days", current.filter((d) => d !== day));
    } else {
      setValue("training_days", [...current, day].sort((a, b) => a - b));
    }
  };

  const onSubmit = async (data: PlanWorkoutForm) => {
    if (!workout?.id) return;

    if (!data.start_date) {
      toast({ variant: "destructive", description: "Please select a start date." });
      return;
    }
    if (!data.training_days.length) {
      toast({ variant: "destructive", description: "Please select at least one training day." });
      return;
    }
    if (!coachId) {
      toast({ variant: "destructive", description: "No coach assigned to this consultation." });
      return;
    }
    if (!memberId) {
      toast({ variant: "destructive", description: "No member selected." });
      return;
    }
    if (!consultationId) {
      toast({ variant: "destructive", description: "Consultation not saved yet." });
      return;
    }

    try {
      await assignWorkoutPlan({
        consultation_id: consultationId,
        org_id: orgId,
        data: {
          workout_id: Number(workout.id),
          org_id: orgId,
          coach_id: coachId,
          member_ids: [memberId],
          start_date: data.start_date,
          training_days: data.training_days,
          weeks: data.weeks,
        },
      }).unwrap();

      toast({ variant: "success", description: "Workout plan created successfully." });
      onClose(workout?.id ? Number(workout.id) : undefined);
    } catch (err: unknown) {
      toast({ variant: "destructive", description: "Failed to create workout plan." });
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-md flex flex-col gap-0 p-0">
        <SheetHeader className="px-6 py-4 border-b">
          <SheetTitle className="flex items-center gap-2 text-base">
            <Dumbbell className="w-4 h-4 text-primary" />
            Plan Workout
          </SheetTitle>
          {workout && (
            <SheetDescription className="text-xs text-gray-500 truncate">
              {workout.workout_name} · {workout.weeks} Weeks
            </SheetDescription>
          )}
        </SheetHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 overflow-y-auto">
          <div className="flex flex-col gap-5 px-6 py-5 flex-1">

            {/* Start Date */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">
                Start Date <span className="text-red-500">*</span>
              </label>
              <Controller
                control={control}
                name="start_date"
                rules={{ required: "Start date is required" }}
                render={({ field }) => (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        type="button"
                        className={cn(
                          "w-full justify-start text-left font-normal h-10",
                          !field.value && "text-gray-400"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 opacity-50" />
                        {field.value
                          ? format(new Date(field.value), "dd MMM yyyy")
                          : "Select start date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value ? new Date(field.value) : undefined}
                        onSelect={(date) => {
                          if (date) field.onChange(format(date, "yyyy-MM-dd"));
                        }}
                        disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                )}
              />
              {errors.start_date && (
                <p className="text-xs text-red-500">{errors.start_date.message}</p>
              )}
            </div>

            <Separator />

            {/* Training Days */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Training Days <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map((day) => {
                  const isSelected = trainingDays?.includes(day.value);
                  return (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => toggleDay(day.value)}
                      className={cn(
                        "w-11 h-11 rounded-full text-sm font-medium border transition-colors",
                        isSelected
                          ? "bg-primary text-white border-primary"
                          : "bg-white text-gray-600 border-gray-300 hover:border-primary/50"
                      )}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
              {errors.training_days && (
                <p className="text-xs text-red-500">{errors.training_days.message}</p>
              )}
            </div>

            <Separator />

            {/* Weeks */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Weeks <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setValue("weeks", Math.max(1, weeks - 1))}
                  className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors text-lg font-medium"
                >
                  −
                </button>
                <span className="min-w-[3rem] text-center text-lg font-semibold text-gray-800">
                  {weeks}
                </span>
                <button
                  type="button"
                  onClick={() => setValue("weeks", weeks + 1)}
                  className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors text-lg font-medium"
                >
                  +
                </button>
                <span className="text-sm text-gray-400">
                  {weeks === 1 ? "week" : "weeks"}
                </span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t flex items-center justify-end gap-3 shrink-0">
            <Button type="button" variant="outline" onClick={() => onClose()}>
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              loading={isLoading}
              className="bg-primary text-white px-6"
            >
              Create Plan
            </LoadingButton>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};

// ─── Main WorkoutPlanStep ──────────────────────────────────────────────────────

const WorkoutPlanStep = ({ action, open }: FormStepProps) => {
  const { setValue, watch, getValues } = useFormContext<Consultation & { assigned_workout_plan_ids?: number[] }>();
  const [searchParams] = useSearchParams();
  const isMobileView = searchParams.get("mobileView") === "true";
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const [hasAutoSelected, setHasAutoSelected] = useState(false);

  // Track which selected workouts have been planned (have start_date, training_days, weeks)
  const [plannedWorkoutIds, setPlannedWorkoutIds] = useState<Set<number>>(new Set());

  // Pass plannedWorkoutIds to parent form context
  useEffect(() => {
    setValue("planned_workout_ids" as any, Array.from(plannedWorkoutIds), {
      shouldDirty: true,
    });
  }, [plannedWorkoutIds, setValue]);

  // Embedded workout wizard state
  const [wizardOpen, setWizardOpen] = useState(false);

  // Plan sheet state
  const [planSheetOpen, setPlanSheetOpen] = useState(false);
  const [planningWorkout, setPlanningWorkout] = useState<WorkoutPlanView | null>(null);

  const {
    searchCriteria,
    setSearchCriteria,
    query,
    inputValue,
    setInputValue,
  } = useTableData<SearchCriteriaType>(initialValue);

  const assignedIds = watch("assigned_workout_plan_ids") || [];
  const memberId = watch("member_id");
  const coachId = watch("coach_id");
  const consultationId = watch("id");

  // ── Fetch goals fresh from the API (form state may be stale/empty) ──
  const { data: goalApiData } = useGetGoalQuery(
    { consultation_id: Number(consultationId), org_id: orgId },
    { skip: !consultationId || !open }
  );

  // goalApiData.goals[].goals[] contains snake_case values e.g. ["lose_weight"]
  // The workout API expects human-readable labels e.g. "Lose Weight"
  const goalLabels = useMemo<string[]>(() => {
    if (!goalApiData?.goals?.length) return [];
    return goalApiData.goals
      .flatMap((g) => g.goals ?? [])
      .map((val) => {
        const match = workoutGoals.find(
          (wg) => wg.value === val || wg.label.toLowerCase() === val.toLowerCase()
        );
        return match?.label ?? null;
      })
      .filter(Boolean) as string[];
  }, [goalApiData]);

  // ── Fetch coach to get category_ids ──
  const { data: coachData } = useGetCoachByIdQuery(
    { org_id: Number(orgId), id: Number(coachId) },
    { skip: !coachId || !open }
  );
  const categoryIds = coachData?.category_ids ?? [];

  // ── Fetch workouts with server-side goal + category filters ──
  const { data: workoutsData, isLoading } = useGetAllWorkoutQuery(
    {
      org_id: Number(orgId),
      query,
      goals: goalLabels.length > 0 ? goalLabels : undefined,
      category_ids: categoryIds.length > 0 ? categoryIds : undefined,
    },
    { skip: !open }
  );

  // Workouts returned by the server are already filtered — use them directly.
  const filteredWorkouts = workoutsData?.data ?? [];

  // Fetch assigned workout plans for this consultation (edit mode)
  const { data: consultationWorkoutPlans } = useGetConsultationWorkoutPlanQuery(
    { consultation_id: Number(consultationId), org_id: orgId },
    { skip: !consultationId || !open }
  );

  // Pre-select workout cards from the saved workout plan (workout_id is the card id)
  useEffect(() => {
    if (consultationWorkoutPlans && consultationWorkoutPlans.length > 0 && assignedIds.length === 0) {
      const preSelectedIds = consultationWorkoutPlans
        .map((p) => p.workout_id)
        .filter((id) => id && !Number.isNaN(id));
      if (preSelectedIds.length > 0) {
        setValue("assigned_workout_plan_ids", preSelectedIds, { shouldValidate: true, shouldDirty: false });
      }
    }
  }, [consultationWorkoutPlans]);

  useEffect(() => {
    if (!isLoading && filteredWorkouts.length > 0 && !hasAutoSelected && memberId) {
      const preSelectedIds: number[] = [];
      filteredWorkouts.forEach((plan) => {
        const planId = Number(plan.id);
        const memberIds = plan.members?.map((m: any) =>
          typeof m === "number" ? m : m.member_id
        ) || [];
        if (planId && !Number.isNaN(planId) && memberIds.includes(Number(memberId))) {
          preSelectedIds.push(planId);
        }
      });
      if (preSelectedIds.length > 0 && assignedIds.length === 0) {
        setValue("assigned_workout_plan_ids", preSelectedIds, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      setHasAutoSelected(true);
    }
  }, [isLoading, filteredWorkouts, memberId, hasAutoSelected, setValue, assignedIds.length]);

  const handleSelect = (planId: number) => {
    if (!planId) return;
    const currentIds = getValues("assigned_workout_plan_ids") || [];
    let newIds = currentIds.includes(planId)
      ? currentIds.filter((id) => id !== planId)
      : [...currentIds, planId];
    newIds = Array.from(new Set(newIds)).filter(
      (id) => typeof id === "number" && !Number.isNaN(id)
    );
    setValue("assigned_workout_plan_ids", newIds, {
      shouldValidate: true,
      shouldDirty: true,
    });

    // Clear planning status when unselecting a workout
    if (currentIds.includes(planId) && !newIds.includes(planId)) {
      setPlannedWorkoutIds((prev) => {
        const next = new Set(prev);
        next.delete(planId);
        return next;
      });
    }
  };

  const handlePlanClick = (e: React.MouseEvent, plan: WorkoutPlanView) => {
    e.stopPropagation(); // don't also toggle selection
    setPlanningWorkout(plan);
    setPlanSheetOpen(true);
  };

  const totalRecords = workoutsData?.filtered_counts || 0;
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const [historyData, setHistoryData] = useState<HistoryConsultation | null>(null);

  return (
    <>
      <div className="space-y-4 h-full flex flex-col">
        {/* <HistoryFilter memberId={memberId!} onDataFound={setHistoryData} /> */}

        {historyData?.workout_plans && historyData.workout_plans.length > 0 && (
          <Card className="p-4 border-[#e2e8f0]">
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-sm text-[#1e293b]">Previous Workout Plans</p>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 uppercase">
                Historical View
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {historyData.workout_plans.map((plan) => (
                <Badge
                  key={plan.id}
                  variant="outline"
                  className="bg-[#f8fafc] border-[#f1f5f9] text-[#1e293b] font-medium py-1"
                >
                  {plan.workout_name}
                </Badge>
              ))}
            </div>
          </Card>
        )}

        <Card className="p-4 flex flex-col h-full bg-white">
          <div className="space-y-4 h-full flex flex-col">
            {/* Search + Create button */}
            <div className="flex items-center gap-4 shrink-0">
              <TableSearchFilter<SearchCriteriaType>
                setSearchCriteria={setSearchCriteria}
                inputValue={inputValue}
                setInputValue={setInputValue}
                placeHolder="Search Workouts"
              />
              <Button 
                      type="button" 
                      className="bg-primary text-sm text-black flex items-center gap-1 h-8 flex-shrink-0"
                      onClick={() => setWizardOpen(true)}
                    >
                      <PlusIcon className="size-4" />
                      <span>Create New</span>
                    </Button>
            </div>

            {/* Workout cards */}
            <div className="rounded-none flex-1 flex flex-col">
              <ScrollArea className="flex-1 min-h-[400px]">
                {isLoading ? (
                  <div className="flex justify-center p-8 text-gray-400">
                    Loading workout plans...
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-1">
                    {/* Goal / category filter info banner */}
                    {(goalLabels.length > 0 || categoryIds.length > 0) && (
                        <div className="col-span-full flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/5 border border-primary/20 text-xs text-primary font-medium mb-1">
                          <Target className="w-3.5 h-3.5 shrink-0" />

                          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-1">
                            <span>Workouts filtered by goals</span>

                            <div className="flex items-center gap-1">
                              {goalLabels.length > 0 && (
                                <span className="text-gray-500 font-normal">
                                  ({goalLabels.join(", ")})
                                </span>
                              )}

                              <span className="text-gray-400 font-normal sm:ml-auto">
                                {filteredWorkouts.length} workouts
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                    {filteredWorkouts.map((plan) => {
                      const planId = Number(plan.id);
                      if (!planId || Number.isNaN(planId)) return null;
                      const isSelected = assignedIds.includes(planId);

                      return (
                        <Card
                          key={planId}
                          className={cn(
                            "cursor-pointer transition-all hover:border-primary/50",
                            isSelected
                              ? "border-primary bg-primary/5 ring-1 ring-primary"
                              : "border-gray-200"
                          )}
                          onClick={() => handleSelect(planId)}
                        >
                          <CardContent className="p-4 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                              {plan.image_url ? (
                                <img
                                  src={plan.image_url}
                                  alt={plan.workout_name}
                                  className="w-12 h-12 rounded-full object-cover bg-gray-100 shrink-0"
                                />
                              ) : (
                                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-400 shrink-0">
                                  No Img
                                </div>
                              )}
                              <div className="flex flex-col min-w-0">
                                <h4 className="font-semibold text-sm text-gray-900 truncate max-w-[130px]">
                                  {plan.workout_name}
                                </h4>
                                <p className="text-[10px] text-gray-500">
                                  {plan.level} · {plan.weeks} Weeks
                                </p>
                                {/* Planning status badge */}
                                {isSelected && (
                                  <Badge
                                    variant={plannedWorkoutIds.has(planId) ? "default" : "destructive"}
                                    className={cn(
                                      "mt-1 text-[9px] px-1.5 py-0 w-fit",
                                      plannedWorkoutIds.has(planId)
                                        ? "bg-green-100 text-green-700 border-green-200"
                                        : "bg-orange-100 text-orange-700 border-orange-200"
                                    )}
                                  >
                                    {plannedWorkoutIds.has(planId) ? "Planned" : "Needs Planning"}
                                  </Badge>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-2 shrink-0">
                              {/* Plan Workout button */}
                              <button
                                type="button"
                                onClick={(e) => handlePlanClick(e, plan)}
                                className={cn(
                                  "text-[10px] font-semibold px-2.5 py-1 rounded-md border transition-colors whitespace-nowrap",
                                  "bg-white border-primary text-primary hover:bg-primary hover:text-white"
                                )}
                              >
                                Plan
                              </button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}

                    {filteredWorkouts.length === 0 && (
                      <div className="col-span-full text-center p-8 text-gray-400 text-sm">
                        {goalLabels.length > 0 || categoryIds.length > 0
                          ? "No workouts found matching the consultation goals or coach categories."
                          : "No workout plans found."}
                      </div>
                    )}
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Pagination */}
            {totalRecords > 0 && (
              <div className="shrink-0 pt-2 border-t mt-auto">
                <Pagination
                  limit={searchCriteria.limit}
                  offset={searchCriteria.offset}
                  totalItems={totalRecords}
                  onLimitChange={handleLimitChange}
                  onNextPage={handleNextPage}
                  onPrevPage={handlePrevPage}
                  onFirstPage={handleFirstPage}
                  onLastPage={handleLastPage}
                  isLastPage={isLastPage}
                />
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Plan Workout Sheet */}
      <PlanWorkoutSheet
        open={planSheetOpen}
        onClose={(workoutId?: number) => {
          setPlanSheetOpen(false);
          setPlanningWorkout(null);
          // Track that this workout has been planned
          if (workoutId) {
            setPlannedWorkoutIds((prev) => new Set([...prev, workoutId]));
          }
        }}
        workout={planningWorkout}
        orgId={orgId}
        coachId={coachId ? Number(coachId) : undefined}
        memberId={memberId ? Number(memberId) : undefined}
        consultationId={consultationId ? Number(consultationId) : undefined}
        existingConsultationPlans={consultationWorkoutPlans}
      />

      {/* Embedded workout wizard — URL stays on /admin/consultation */}
      <EmbeddedWorkoutWizard
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onComplete={(newWorkoutId) => {
          // Auto-select the freshly created workout card
          const currentIds = getValues("assigned_workout_plan_ids") || [];
          if (!currentIds.includes(newWorkoutId)) {
            setValue("assigned_workout_plan_ids", [...currentIds, newWorkoutId], {
              shouldValidate: true,
              shouldDirty: true,
            });
          }
          setWizardOpen(false);
        }}
        initialGoal={goalLabels.length > 0 ? goalLabels[0] : undefined}
        initialCategoryId={categoryIds.length === 1 ? categoryIds[0] : undefined}
        categoryIds={categoryIds.length > 0 ? categoryIds : undefined}
      />
    </>
  );
};

export default WorkoutPlanStep;